/*
 * Wishlist analytics.
 *
 * Sends wishlist and cart events to the collector, and stamps cart lines that
 * came from a wishlisted product so the attribution survives into the order.
 *
 * Nothing is stored or sent until Shopify's Customer Privacy API reports that
 * analytics processing is allowed — including the visitor ID itself, which is
 * only generated once consent exists.
 */
window.WishlistAnalytics = (function () {
  const VISITOR_KEY = 'vlj_vid';
  const config = window.vljAnalytics || {};
  const queue = [];
  let consent = null;

  function visitorId() {
    let id;
    try {
      id = localStorage.getItem(VISITOR_KEY);
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(VISITOR_KEY, id);
      }
    } catch (error) {
      // Private mode or blocked storage: stay session-only rather than failing.
      id = id || crypto.randomUUID();
    }
    return id;
  }

  function storedVisitorId() {
    try {
      return localStorage.getItem(VISITOR_KEY);
    } catch (error) {
      return null;
    }
  }

  function analyticsAllowed() {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.analyticsProcessingAllowed !== 'function') return null;
    try {
      return api.analyticsProcessingAllowed();
    } catch (error) {
      return null;
    }
  }

  function flush() {
    if (consent !== true || !config.endpoint || !queue.length) return;

    const id = visitorId();
    const batch = queue.splice(0, queue.length).map((event) => ({ ...event, visitor_id: id }));

    fetch(config.endpoint, {
      method: 'POST',
      keepalive: true,
      mode: 'cors',
      // text/plain keeps this a simple request, so there is no preflight per event.
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(batch),
    }).catch(() => {
      // Analytics must never interfere with the storefront.
    });
  }

  function track(type, product) {
    if (!product || !product.id) return;
    if (consent === false) return;

    queue.push({
      type: type,
      product_id: String(product.id),
      variant_id: product.variantId ? String(product.variantId) : null,
      handle: product.handle || null,
      customer_id: config.customerId || null,
    });

    flush();
  }

  function updateConsent() {
    const allowed = analyticsAllowed();
    if (allowed === null) return;
    consent = allowed;
    if (allowed) flush();
    else queue.length = 0;
  }

  /*
   * Stamped just before submit rather than kept in sync continuously: a capture
   * listener on the document runs before product-form's own submit handler
   * builds its FormData, and it works for forms injected later by quick-add.
   *
   * Disabled inputs are excluded from FormData, which is how Dawn's gift-card
   * recipient form handles optional line item properties too.
   */
  function stampForm(form) {
    const host = form.closest('product-form[data-product-id]');
    if (!host) return;

    const flag = form.querySelector('[data-wishlist-flag]');
    const vid = form.querySelector('[data-wishlist-vid]');
    if (!flag || !vid) return;

    const wishlisted = window.Wishlist && Wishlist.has(host.dataset.productId);
    const id = consent === true ? storedVisitorId() : null;

    flag.disabled = !wishlisted;
    vid.disabled = !wishlisted || !id;
    vid.value = id || '';
  }

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) stampForm(event.target);
    },
    true
  );

  document.addEventListener('visitorConsentCollected', updateConsent);

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], updateConsent);
  } else {
    updateConsent();
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
    const cart = payload && payload.cartData;
    if (!cart || !cart.product_id) return;
    if (!window.Wishlist || !Wishlist.has(cart.product_id)) return;

    track('wishlist_add_to_cart', {
      id: cart.product_id,
      variantId: cart.variant_id || cart.id,
      handle: cart.handle,
    });
  });

  return { track: track };
})();
